import { formatMoney } from '@theme/money-formatting';

/**
 * Coupon-price decorator — the storefront half of the "show the influencer
 * discount on the shelf" A/B test.
 *
 * The theme never decides who is in the test, and never remembers: the A/B
 * platform re-runs its custom code on every page view, so this module obeys
 * whatever it finds on the page it is on. Assignment arrives through the shared,
 * vendor-agnostic A/B handle from GS-2695 (assets/analytics-experiments.js), so
 * the same variant that drives the prices also lands on every funnel event as
 * `ab_*` props — the experiment can be read in Plausible/GA4 independently of
 * the A/B tool's own numbers.
 *
 * The A/B platform's custom code, in the treatment group only (the signal is
 * kept for the rest of the visit — see isTreatment — so it only has to run on
 * the landing page):
 *
 *   var e = window.gogroupExperiments;
 *   if (e && typeof e.assign === 'function') e.assign('coupon_price', 'b');
 *   else (window.gogroupExperiments = e || []).push(['coupon_price', 'b']);
 *   window.dispatchEvent(new Event('go:ab-coupon-price'));
 *
 * Both shapes are handled here (the drained API and the array-shaped queue left
 * behind when the platform loads before the analytics layer). The control group
 * signals nothing, so this file does nothing and the page is byte-for-byte what
 * it is today. QA door: `?ab=coupon_price:b`, the convention already used by the
 * A/B layer.
 *
 * Where the percentage comes from: the coupon itself. The Yampi API needs
 * server-side credentials, so the browser asks a small lookup service instead
 * (`ab_coupon_price_lookup_url`, a GoDeploy app), which answers
 * `{ valid: true, percent }` only for an active, unrestricted percentage coupon
 * and `{ valid: false }` for anything else. The lookup runs only once the
 * visitor is confirmed in the treatment group, at most once per page, and is
 * cached in sessionStorage — so the control group never makes the request.
 * Any failure (invalid coupon, timeout, service down) leaves the page exactly
 * as the control sees it: a discount the checkout will not honour is worse
 * than no discount at all.
 *
 * What it rewrites: every element carrying `data-ab-price` (rendered by
 * snippets/ab-coupon-price-attrs.liquid) holds the cents value it is CURRENTLY
 * displaying. Applying the percentage to that — rather than to the raw variant
 * price — is what makes the coupon compound over the store's existing pricing
 * (PIX, installments, cart totals) with one rule and no per-surface math.
 *
 * Display only. No form value, cart payload or checkout URL is touched — the
 * discount itself is still applied by Yampi from the `promocode` that
 * snippets/influencer-discount.liquid put in the `utmsTrack` cookie.
 */

const PRICE_SELECTOR = '[data-ab-price]:not([data-ab-price-applied])';
const WAS_SELECTOR = '[data-ab-price-was]';
const HIDE_SELECTOR = '[data-ab-price-hide]';
const SAVINGS_SELECTOR = '[data-ab-savings]:not([data-ab-savings-applied])';
const LABEL_SELECTOR = '[data-ab-price-label]';
const CODE_SELECTOR = '[data-ab-price-code]';
/**
 * The visit: which coupon the visitor arrived with and which variant the URL
 * forced. A cookie rather than sessionStorage so it holds across tabs (a
 * product opened in a new tab must not fall back to full price), with a
 * sliding 30-minute expiry — the same "session" the analytics tools count — so
 * a visitor who comes back tomorrow without the influencer link sees normal
 * prices. Deliberately NOT Yampi's 30-day `utmsTrack`: that one keeps the
 * checkout discount (attribution); this one only scopes the shelf display.
 */
const SESSION_COOKIE = 'ab_coupon_price';
const SESSION_MAX_AGE_S = 30 * 60;
const SESSION_COUPON = 'c';
const SESSION_VARIANT = 'v';
/** Mirrors snippets/influencer-discount.liquid — the codes that reach checkout. */
const VALID_CODE = /^[A-Za-z0-9._-]{1,64}$/;
const EXPERIMENT = 'coupon_price';
/** Variant ids that mean "leave the prices alone" — everything else is treatment. */
const CONTROL_VARIANTS = new Set(['a', 'control', 'off', '0']);
/** Same lifetime as the lookup service's own cache, so the two never disagree for long. */
const LOOKUP_CACHE_TTL_MS = 10 * 60 * 1000;
const LOOKUP_CACHE_PREFIX = 'ab-coupon-price:';
/**
 * A cold lookup measures ~2 s end to end (hosting gateway + Yampi). Past this
 * the prices would visibly jump long after the page settled — give up instead.
 * Only the first page of a session pays it; later pages hit sessionStorage.
 */
const LOOKUP_TIMEOUT_MS = 4000;

/**
 * @typedef {Object} CouponPriceBaseConfig
 * @property {string} lookupUrl - Endpoint that resolves a coupon to its percentage
 * @property {string} shop - `shop.permanent_domain`, which the lookup service keys stores by
 * @property {string} moneyFormat - Shopify money format template
 * @property {string} moneyFormatWithCurrency - Same, for surfaces that print the currency code
 * @property {string} currency - ISO currency code
 */

/**
 * @typedef {CouponPriceBaseConfig & { coupon: string, percent: number }} CouponPriceConfig
 * `coupon` is the code this visitor arrived with; `percent` is its real
 * percentage, as resolved by the lookup service.
 */

/**
 * Reads the Liquid-rendered configuration blob. Neither the coupon nor its
 * percentage is in it — the coupon comes from the visitor's URL (kept for the
 * visit), the percentage from the lookup service.
 * @returns {CouponPriceBaseConfig | null}
 */
function readConfig() {
  const node = document.getElementById('ab-coupon-price-config');
  if (!node?.textContent) return null;

  try {
    const parsed = JSON.parse(node.textContent);
    if (typeof parsed.lookupUrl !== 'string' || new URL(parsed.lookupUrl).protocol !== 'https:') return null;
    if (typeof parsed.shop !== 'string' || parsed.shop === '') return null;
    if (typeof parsed.moneyFormat !== 'string' || typeof parsed.currency !== 'string') return null;
    const withCurrency =
      typeof parsed.moneyFormatWithCurrency === 'string' ? parsed.moneyFormatWithCurrency : parsed.moneyFormat;
    return {
      lookupUrl: parsed.lookupUrl,
      shop: parsed.shop,
      moneyFormat: parsed.moneyFormat,
      moneyFormatWithCurrency: withCurrency,
      currency: parsed.currency,
    };
  } catch {
    return null;
  }
}

/**
 * @param {string} name
 * @returns {string | null}
 */
function readCookie(name) {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trimStart();
    if (trimmed.startsWith(prefix)) return trimmed.slice(prefix.length);
  }
  return null;
}

/** @returns {URLSearchParams} The current visit's values (empty when there is no visit). */
function readSession() {
  try {
    return new URLSearchParams(decodeURIComponent(readCookie(SESSION_COOKIE) ?? ''));
  } catch {
    return new URLSearchParams();
  }
}

/**
 * Writes the visit back with a fresh 30-minute expiry, or deletes it when it
 * holds nothing. Called on every page view that has a visit, which is what
 * makes the expiry sliding (30 minutes of INACTIVITY, not since landing).
 * @param {URLSearchParams} session
 */
function writeSession(session) {
  const value = session.toString();
  const maxAge = value ? SESSION_MAX_AGE_S : 0;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${SESSION_COOKIE}=${encodeURIComponent(value)}; Max-Age=${maxAge}; path=/; SameSite=Lax${secure}`;
}

/**
 * The coupon this visitor arrived with ON THIS VISIT, or '' when there is none
 * — in which case nothing is rewritten.
 *
 * The URL covers the landing page and is copied into the visit cookie so the
 * pages after it keep the discount. A visitor who returns later without the
 * influencer link gets '' here, even though Yampi's own 30-day cookie still
 * applies the coupon at checkout: the test is about what the landing session
 * shows. An empty or malformed `?coupon_code=` clears it, mirroring
 * snippets/influencer-discount.liquid (same character set — a code that would
 * not survive to the checkout must not be shown on the shelf).
 * @returns {string}
 */
function readCoupon() {
  const session = readSession();
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('coupon_code');
    if (fromUrl !== null) {
      const valid = VALID_CODE.test(fromUrl);
      if (valid) session.set(SESSION_COUPON, fromUrl);
      else session.delete(SESSION_COUPON);
      writeSession(session);
      return valid ? fromUrl : '';
    }
  } catch {
    /* malformed query string — fall back to the visit */
  }
  const stored = session.get(SESSION_COUPON) ?? '';
  return VALID_CODE.test(stored) ? stored : '';
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeVariant(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * `?ab=<experiment>:<variant>` — the QA door (and the convention the A/B layer
 * already defines), so there is one way to force a variant, not two.
 * @returns {string}
 */
function variantFromUrl() {
  try {
    for (const entry of new URLSearchParams(window.location.search).getAll('ab')) {
      const [id, variant] = entry.split(':');
      if (normalizeVariant(id) === EXPERIMENT) return normalizeVariant(variant);
    }
  } catch {
    /* malformed query string */
  }
  return '';
}

/**
 * Reads the shared A/B handle in either shape: the drained API, or the
 * array-shaped queue the platform leaves when it loads before the analytics
 * layer (and which never drains on a store that has no analytics configured).
 * @returns {string}
 */
function variantFromLayer() {
  const handle = /** @type {any} */ (window).gogroupExperiments;
  if (!handle) return '';

  if (Array.isArray(handle)) {
    for (const entry of handle) {
      if (Array.isArray(entry) && normalizeVariant(entry[0]) === EXPERIMENT) return normalizeVariant(entry[1]);
    }
    return '';
  }

  if (typeof handle.list !== 'function') return '';
  try {
    for (const entry of handle.list()) {
      if (normalizeVariant(entry?.experiment) === EXPERIMENT) return normalizeVariant(entry?.variant);
    }
  } catch {
    /* the layer is not ready — the retry on `go:ab-coupon-price` covers it */
  }
  return '';
}

/**
 * The visitor's variant for this visit. A live signal — the URL, or the A/B
 * platform's custom code through the shared layer — wins and is written to the
 * visit cookie; without one, the variant recorded earlier in the visit applies.
 *
 * Recording the live signal is what keeps the treatment on the pages after the
 * landing one: the platform targets visitors whose URL carries `coupon_code`,
 * so its custom code typically runs on that first page only, and the `?ab=`
 * parameter is gone after the first click too. Visit-scoped on purpose (see
 * SESSION_COOKIE): a later visit starts clean. `?ab=coupon_price:a` switches it
 * back within the visit.
 * @returns {boolean}
 */
function isTreatment() {
  const live = variantFromUrl() || variantFromLayer();
  const session = readSession();
  if (live && session.get(SESSION_VARIANT) !== live) {
    session.set(SESSION_VARIANT, live);
    writeSession(session);
  }
  const variant = live || normalizeVariant(session.get(SESSION_VARIANT));
  return Boolean(variant) && !CONTROL_VARIANTS.has(variant);
}

/**
 * @param {unknown} value
 * @returns {number | null} The percentage when it is one the shelf can show
 */
function usablePercent(value) {
  const percent = Number(value);
  return Number.isFinite(percent) && percent > 0 && percent < 100 ? percent : null;
}

/**
 * @param {string} coupon
 * @returns {{ percent: number | null } | null} The cached answer, or null on a miss
 */
function readCachedPercent(coupon) {
  try {
    const entry = JSON.parse(window.sessionStorage.getItem(LOOKUP_CACHE_PREFIX + coupon) ?? 'null');
    if (!entry || typeof entry.t !== 'number' || Date.now() - entry.t > LOOKUP_CACHE_TTL_MS) return null;
    return { percent: usablePercent(entry.percent) };
  } catch {
    return null;
  }
}

/**
 * Negative answers are cached too, so an invalid code costs one request per
 * session instead of one per page.
 * @param {string} coupon
 * @param {number | null} percent
 */
function writeCachedPercent(coupon, percent) {
  try {
    window.sessionStorage.setItem(LOOKUP_CACHE_PREFIX + coupon, JSON.stringify({ percent, t: Date.now() }));
  } catch {
    /* storage full or blocked — the lookup simply runs again next page */
  }
}

/**
 * Resolves to the coupon's percentage, or null when the service ANSWERED that
 * it is not usable (`200 { valid: false }`) — the only outcome worth caching.
 * Anything else (5xx, 429, network error, timeout) rejects, so a hiccup in the
 * service or in Yampi never gets remembered as an invalid coupon and the next
 * page simply asks again.
 * @param {string} coupon
 * @param {CouponPriceBaseConfig} config
 * @returns {Promise<number | null>}
 */
async function fetchPercent(coupon, config) {
  const url = new URL(config.lookupUrl);
  url.searchParams.set('shop', config.shop);
  url.searchParams.set('code', coupon);

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
  try {
    const response = await fetch(url, { credentials: 'omit', signal: controller.signal });
    if (!response.ok) throw new Error(`lookup ${response.status}`);
    const body = await response.json();
    return body?.valid === true ? usablePercent(body.percent) : null;
  } finally {
    window.clearTimeout(timeout);
  }
}

/** @type {Promise<number | null> | null} */
let percentLookup = null;

/**
 * The coupon's real percentage, or null when the shelf must not show one.
 * Memoized, so the treatment signal firing more than once on a page never
 * turns into more than one request.
 * @param {string} coupon
 * @param {CouponPriceBaseConfig} config
 * @returns {Promise<number | null>}
 */
function resolvePercent(coupon, config) {
  if (percentLookup) return percentLookup;

  const cached = readCachedPercent(coupon);
  if (cached) {
    percentLookup = Promise.resolve(cached.percent);
    return percentLookup;
  }

  percentLookup = fetchPercent(coupon, config).then(
    (percent) => {
      writeCachedPercent(coupon, percent);
      return percent;
    },
    // Failures are not cached: the service being briefly unreachable says
    // nothing about the coupon.
    () => null
  );
  return percentLookup;
}

/**
 * Integer-cents math, mirroring the Liquid convention used across the theme
 * (`price | times: percent | divided_by: 100`), so the JS and the server-side
 * PIX price never disagree by a cent.
 * @param {number} cents
 * @param {number} percent
 * @returns {number}
 */
function applyDiscount(cents, percent) {
  return cents - Math.floor((cents * percent) / 100);
}

/**
 * A savings statement restated against the couponed price — the PDP savings
 * pill (blocks/price.liquid) and the sale "%" badge (snippets/product-badges.liquid).
 * Liquid publishes the raw compare-at and price plus the template (with its
 * `[save_amount]` / `[save_percent]` placeholders), so the copy stays the
 * merchant's and only the numbers are recomputed — with the same rounding as
 * the Liquid, so the two never disagree on a variant.
 * @param {HTMLElement} element
 * @param {CouponPriceConfig} config
 */
function restateSavings(element, config) {
  element.dataset.abSavingsApplied = '';
  try {
    const parsed = JSON.parse(element.dataset.abSavings ?? '');
    const compareAt = Number(parsed?.compareAt);
    const price = Number(parsed?.price);
    const template = typeof parsed?.template === 'string' ? parsed.template : '';
    if (!Number.isFinite(compareAt) || !Number.isFinite(price) || compareAt <= 0 || template === '') return;

    const saving = compareAt - applyDiscount(price, config.percent);
    if (saving <= 0) return;
    const percent = Math.round((saving * 100) / compareAt);

    // The template is theme-controlled — the merchant's rich-text setting, or
    // the badge's fixed format — and the substitutions are a formatted amount
    // and an integer, so nothing visitor-controlled gets in.
    element.innerHTML = template
      .replaceAll('[save_amount]', formatMoney(saving, config.moneyFormat, config.currency))
      .replaceAll('[save_percent]', String(percent));
  } catch {
    /* unreadable payload — the pill keeps its original, still-true copy */
  }
}

/** Money formats and locale strings disagree about non-breaking spaces. */
const normalizeSpaces = (/** @type {string} */ value) => value.replace(/\u00a0/g, ' ');

/**
 * Swaps one money substring inside an element. Targets are either a leaf span
 * ("R$ 189,91") or a whole interpolated sentence \u2014 and those sentences are not
 * plain text: the PDP installments line wraps an SVG card icon, and the
 * `*_html` locale strings put the value inside a `<strong>`. So the swap
 * happens on the single TEXT NODE holding the money, never on the element's
 * textContent, which would delete those children.
 * @param {HTMLElement} element
 * @param {string} from
 * @param {string} to
 * @returns {boolean} Whether the text was changed
 */
function replaceMoney(element, from, to) {
  /** @type {Text[]} */
  const textNodes = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(/** @type {Text} */ (node));
  }

  for (const node of textNodes) {
    const text = node.nodeValue ?? '';
    if (text.includes(from)) {
      node.nodeValue = text.replace(from, to);
      return true;
    }
  }

  // Exact match failed — retry ignoring non-breaking-space differences.
  const needle = normalizeSpaces(from);
  for (const node of textNodes) {
    const text = node.nodeValue ?? '';
    const index = normalizeSpaces(text).indexOf(needle);
    if (index === -1) continue;
    node.nodeValue = text.slice(0, index) + to + text.slice(index + from.length);
    return true;
  }

  return false;
}

/**
 * Matches inside a subtree AND the subtree root itself — the observer hands
 * over inserted nodes, and a re-rendered price container is often the match.
 * @param {ParentNode} root
 * @param {string} selector
 * @returns {HTMLElement[]}
 */
function collect(root, selector) {
  const found = /** @type {HTMLElement[]} */ ([...root.querySelectorAll(selector)]);
  if (root instanceof HTMLElement && root.matches(selector)) found.unshift(root);
  return found;
}

/**
 * The amounts one element states, as written by
 * snippets/ab-coupon-price-attrs.liquid. Usually one; the PDP installments line
 * states two ("R$ 199,90 ou 4x de R$ 49,97 sem juros").
 * @param {HTMLElement} element
 * @returns {Array<{ cents: number, text: string, withCurrency: boolean }>}
 */
function readAmounts(element) {
  try {
    const parsed = JSON.parse(element.dataset.abPrice ?? '');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => ({
        cents: Number(entry?.cents),
        text: String(entry?.text ?? ''),
        withCurrency: entry?.withCurrency === true,
      }))
      .filter((entry) => Number.isFinite(entry.cents) && entry.cents > 0 && entry.text !== '');
  } catch {
    return [];
  }
}

/**
 * @param {ParentNode} root
 * @param {CouponPriceConfig} config
 */
function decorate(root, config) {
  for (const element of collect(root, PRICE_SELECTOR)) {
    // Marked up front, and whatever the outcome: a value that could not be
    // matched will not match on a retry either, and the guard is what stops the
    // observer from discounting the same number twice.
    element.dataset.abPriceApplied = '';

    for (const { cents, text, withCurrency } of readAmounts(element)) {
      // The element states which template Liquid used, so a total rendered as
      // "R$ 199,90 BRL" comes back out with its suffix intact.
      const format = withCurrency ? config.moneyFormatWithCurrency : config.moneyFormat;
      const to = formatMoney(applyDiscount(cents, config.percent), format, config.currency);
      replaceMoney(element, text, to);
    }
  }

  // Reveal the "was" price on products with no compare-at, so the lower number
  // reads as a discount instead of just a cheaper product.
  for (const element of collect(root, WAS_SELECTOR)) element.hidden = false;

  // Restate the PDP savings pill and the sale badge against the couponed price.
  for (const element of collect(root, SAVINGS_SELECTOR)) restateSavings(element, config);

  // Hide what cannot be restated correctly — see the savings pill in
  // snippets/cart-products.liquid.
  for (const element of collect(root, HIDE_SELECTOR)) element.hidden = true;

  // "Com cupom ACHISMOS" — name the coupon next to the price, so the lower
  // number is attributed to the link the shopper followed.
  for (const element of collect(root, LABEL_SELECTOR)) {
    const slot = element.matches(CODE_SELECTOR) ? element : element.querySelector(CODE_SELECTOR);
    if (!slot) continue;
    slot.textContent = config.coupon;
    element.hidden = false;
  }
}

/**
 * Re-applies to an already-processed element whose text was rewritten from
 * underneath us. Self-limiting: after a successful pass the original money
 * string is gone, so our own edit never triggers another one.
 * @param {Node} target - The mutated text node
 * @param {CouponPriceConfig} config
 */
function refresh(target, config) {
  const host = target.parentElement?.closest('[data-ab-price]');
  if (!(host instanceof HTMLElement)) return;

  const text = host.textContent ?? '';
  const reverted = readAmounts(host).some(({ text: amount }) => text.includes(amount));
  if (!reverted) return;

  delete host.dataset.abPriceApplied;
  decorate(host, config);
}

function start() {
  const base = readConfig();
  if (!base) return;

  // Renew the visit on every page view, so it only ends after 30 minutes
  // without navigating.
  const session = readSession();
  if (session.toString()) writeSession(session);

  const coupon = readCoupon();
  if (!coupon) return;

  let observer = /** @type {MutationObserver | null} */ (null);

  const run = async () => {
    // Checked before the lookup, so the control group never makes the request.
    if (!isTreatment()) return;

    const percent = await resolvePercent(coupon, base);
    if (percent === null) return;

    /** @type {CouponPriceConfig} */
    const config = { ...base, coupon, percent };

    decorate(document.body, config);

    if (observer) return;
    // Prices are re-rendered constantly — variant change swaps
    // [ref="priceContainer"] (assets/product-price.js), the cart re-renders via
    // section rendering, quick-add and pagination inject fresh cards. Observing
    // the tree is the one hook that covers all of them.
    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          refresh(mutation.target, config);
          continue;
        }
        for (const node of mutation.addedNodes) {
          // A bare text node means someone assigned `textContent` — that swaps
          // the node instead of editing it, so it arrives here rather than on
          // the characterData branch. assets/upsell-sticky-sync.js does exactly
          // that when it mirrors an upsell into the sticky bar.
          if (node instanceof Element) decorate(node, config);
          else refresh(node, config);
        }
      }
    });
    // `characterData` covers the scripts that write a price straight into an
    // existing node — assets/upsell-sticky-sync.js does exactly that when an
    // upsell is picked. No element is inserted, so the childList branch would
    // never see it and the full price would silently come back.
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  run();

  // The platform's custom code may load after this module; the event lets it
  // hand over the assignment whenever it is ready.
  window.addEventListener('go:ab-coupon-price', run);

  // THIS MODULE KEEPS NO MEMORY BEYOND THE VISIT (see SESSION_COOKIE): the
  // coupon and a URL-forced variant last 30 minutes of inactivity, never
  // longer. It does not persist the platform's assignment a second time — it
  // reads whatever the shared layer reports on the current page view. And since
  // a later visit without the influencer link has no coupon, the discount never
  // reappears on its own.
  //
  // But the layer itself IS sticky: assets/analytics-experiments.js keeps
  // assignments in localStorage for 30 days and never overwrites an existing
  // one, so `list()` keeps returning `coupon_price: b` after the platform stops
  // injecting. HOW TO END THE TEST: turn `ab_coupon_price_enabled` off in the
  // theme settings. Removing the platform's snippet alone is NOT enough —
  // already-bucketed visitors who still carry a coupon in `utmsTrack` would go
  // on seeing discounted prices until their stored assignment expires.
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
